import { PropsWithChildren } from 'react';
import { makeStyles } from '@material-ui/core';
import HomeIcon from '@material-ui/icons/Home';
import ExtensionIcon from '@material-ui/icons/Extension';
import LibraryBooks from '@material-ui/icons/LibraryBooks';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import RadarIcon from '@material-ui/icons/TrackChanges';
import LogoFull from './LogoFull';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import { SidebarSearchModal } from '@backstage/plugin-search';
import {
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarScrollWrapper,
  SidebarSpace,
  SidebarSubmenu,
  SidebarSubmenuItem,
  useSidebarOpenState,
  Link,
} from '@backstage/core-components';
import { FeatureFlagged } from '@backstage/core-app-api';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import { MyGroupsSidebarItem } from '@backstage/plugin-org';
import GroupIcon from '@material-ui/icons/People';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import { Administration } from '@backstage-community/plugin-rbac';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import BuildIcon from '@material-ui/icons/Build';
import VpnKeyIcon from '@material-ui/icons/VpnKey';
import LabelOffIcon from '@material-ui/icons/LabelOff';
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser';
import VisibilityIcon from '@material-ui/icons/Visibility';
import CloudIcon from '@material-ui/icons/Cloud';
import StorageIcon from '@material-ui/icons/Storage';
// import MoneyIcon from '@material-ui/icons/MonetizationOn';
import { ASPMDashboardSidebarItem } from '@appsecgenius/plugin-aspm-frontend';

const useSidebarLogoStyles = makeStyles({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14,
  },
  link: {
    width: sidebarConfig.drawerWidthClosed,
    marginLeft: 24,
  },
});

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();

  if (!isOpen) {
    return null;
  }

  return (
    <div className={classes.root}>
      <Link to="/" underline="none" className={classes.link} aria-label="Home">
        <LogoFull />
      </Link>
    </div>
  );
};

export const Root = ({ children }: PropsWithChildren<{}>) => (
  <SidebarPage>
    <Sidebar>
      <SidebarLogo />
      <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
        <SidebarSearchModal />
      </SidebarGroup>
      <SidebarDivider />
      <SidebarGroup label="Menu" icon={<MenuIcon />}>
        {/* Global nav, not org-specific */}
        <SidebarItem icon={HomeIcon} to="catalog" text="Home" />
        <MyGroupsSidebarItem
          singularTitle="My Group"
          pluralTitle="My Groups"
          icon={GroupIcon}
        />
        <SidebarItem icon={ExtensionIcon} to="api-docs" text="APIs" />
        <SidebarItem icon={LibraryBooks} to="docs" text="Docs" />
        <SidebarItem icon={RadarIcon} to="tech-radar" text="Radars" />
        <SidebarItem icon={AccountTreeIcon} to="org-teams" text="Org Teams" />
        <SidebarItem icon={CreateComponentIcon} to="create" text="Create..." />
        <SidebarItem icon={VisibilityIcon} text="Views">
          <SidebarSubmenu title="Views">
            <SidebarSubmenuItem
              title="Account Access"
              to="/account-access"
              icon={VpnKeyIcon}
            />
            <SidebarSubmenuItem
              title="AWS Tags Audit"
              to="/aws-resource-tags"
              icon={LabelOffIcon}
            />
            <SidebarSubmenuItem
              title="EKS Clusters"
              to="/eks-clusters"
              icon={StorageIcon}
            />
            <SidebarSubmenuItem
              title="EKS Cluster Radar"
              to="/tech-radar/default/radar/genius-sports-eks-cluster-radar"
              icon={RadarIcon}
            />
            <SidebarSubmenuItem
              title="Certificate Management"
              to="/certificate-management"
              icon={VerifiedUserIcon}
            />
            <SidebarSubmenuItem
              title="StreamNative"
              to="/streamnative"
              icon={CloudIcon}
            />
            {/* <SidebarSubmenuItem
              title="Budgeting"
              to="/budgeting"
              icon={MoneyIcon}
            /> */}
          </SidebarSubmenu>
        </SidebarItem>
        <ASPMDashboardSidebarItem />

        {/* End global nav */}
        <SidebarDivider />
        <SidebarScrollWrapper>
          {/* Items in this group will be scrollable if they run out of space */}
          <Administration />
        </SidebarScrollWrapper>
      </SidebarGroup>
      <SidebarSpace />
      <SidebarDivider />
      <SidebarGroup
        label="Settings"
        icon={<UserSettingsSignInAvatar />}
        to="/settings"
      >
        <SidebarSettings />
        <FeatureFlagged with="devtools-enabled">
          <SidebarItem icon={BuildIcon} to="devtools" text="DevTools" />
        </FeatureFlagged>
        <SidebarItem icon={ExitToAppIcon} to="/logout" text="Sign out" />
      </SidebarGroup>
    </Sidebar>
    {children}
  </SidebarPage>
);
